import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { cx, cssVars, isBrowserEnvironment } from '../internal/utils.js';
const ThemeContext = createContext(null);
const STORAGE_KEY = 'nimeet.ui-kit.theme';
function readStoredMode(storageKey) {
    if (!isBrowserEnvironment())
        return null;
    try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored === 'light' || stored === 'dark' || stored === 'system')
            return stored;
    }
    catch {
        // Private mode / sandboxed iframes: fall back to the OS preference.
    }
    return null;
}
function systemTheme() {
    if (!isBrowserEnvironment() || typeof window.matchMedia !== 'function')
        return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
function themeStyle(tokens, accent) {
    const vars = {};
    if (accent)
        vars['--nk-accent'] = accent;
    if (tokens.accent)
        vars['--nk-accent'] = tokens.accent;
    if (tokens.accentHover)
        vars['--nk-accent-hover'] = tokens.accentHover;
    if (tokens.accentSoft)
        vars['--nk-accent-soft'] = tokens.accentSoft;
    if (tokens.accentContrast)
        vars['--nk-accent-contrast'] = tokens.accentContrast;
    if (tokens.background)
        vars['--nk-bg'] = tokens.background;
    if (tokens.surface)
        vars['--nk-surface'] = tokens.surface;
    if (tokens.text)
        vars['--nk-text'] = tokens.text;
    if (tokens.textMuted)
        vars['--nk-text-muted'] = tokens.textMuted;
    if (tokens.border)
        vars['--nk-border'] = tokens.border;
    if (tokens.radius)
        vars['--nk-radius'] = tokens.radius;
    if (typeof tokens.spaceUnit === 'number' && Number.isFinite(tokens.spaceUnit) && tokens.spaceUnit > 0) {
        const unit = tokens.spaceUnit;
        vars['--nk-space-1'] = `${unit}px`;
        vars['--nk-space-2'] = `${unit * 2}px`;
        vars['--nk-space-3'] = `${unit * 3}px`;
        vars['--nk-space-4'] = `${unit * 4}px`;
        vars['--nk-space-5'] = `${unit * 6}px`;
        vars['--nk-space-6'] = `${unit * 8}px`;
    }
    return cssVars(vars);
}
/**
 * Light/dark/system theme provider. Light is the default when the user has never chosen a mode:
 * the kit is light-first and deliberately does **not** follow `prefers-color-scheme` until the host
 * or the user selects the explicit `system` mode. It scopes every design token to a wrapper element
 * and, by default, mirrors only `data-nk-theme` + colour-scheme on `<html>` (never the layout tokens).
 */
export function ThemeProvider({ children, defaultMode, mode: controlledMode, onModeChange, accent, tokens = {}, storageKey = STORAGE_KEY, persist = true, applyToDocument = true, className, }) {
    const [storedMode] = useState(() => readStoredMode(storageKey));
    // Light-first: only an explicit stored choice (or the host) can start in dark/system mode.
    const [internalMode, setInternalMode] = useState(() => storedMode ?? defaultMode ?? 'light');
    const mode = controlledMode ?? internalMode;
    const [system, setSystem] = useState(() => systemTheme());
    const resolvedTheme = mode === 'system' ? system : mode;
    const effectiveAccent = accent ?? tokens.accent ?? null;
    useEffect(() => {
        if (!isBrowserEnvironment() || typeof window.matchMedia !== 'function')
            return undefined;
        const query = window.matchMedia('(prefers-color-scheme: dark)');
        const update = () => setSystem(query.matches ? 'dark' : 'light');
        update();
        if (typeof query.addEventListener === 'function') {
            query.addEventListener('change', update);
            return () => query.removeEventListener('change', update);
        }
        query.addListener(update);
        return () => query.removeListener(update);
    }, []);
    useEffect(() => {
        if (!isBrowserEnvironment())
            return;
        if (!applyToDocument)
            return;
        const root = document.documentElement;
        root.dataset.nkTheme = resolvedTheme;
        root.style.colorScheme = resolvedTheme;
        return () => {
            delete root.dataset.nkTheme;
            root.style.colorScheme = '';
        };
    }, [resolvedTheme, applyToDocument]);
    useEffect(() => {
        if (mode === 'system')
            return;
        if (!persist)
            return;
        if (!isBrowserEnvironment())
            return;
        try {
            window.localStorage.setItem(storageKey, mode);
        }
        catch {
            // Persisting is best-effort.
        }
    }, [mode, persist, storageKey]);
    const setMode = useCallback((next) => {
        if (controlledMode === undefined)
            setInternalMode(next);
        onModeChange?.(next);
    }, [controlledMode, onModeChange]);
    const toggleTheme = useCallback(() => {
        setMode(resolvedTheme === 'dark' ? 'light' : 'dark');
    }, [resolvedTheme, setMode]);
    const context = useMemo(() => ({ mode, resolvedTheme, accent: effectiveAccent, setMode, toggleTheme, tokens }), [mode, resolvedTheme, effectiveAccent, setMode, toggleTheme, tokens]);
    return (_jsx(ThemeContext.Provider, { value: context, children: _jsx("div", { className: cx('nk-root', 'nk-theme', className), "data-nk-theme": resolvedTheme, "data-nk-mode": mode, style: themeStyle(tokens, accent ?? null), children: children }) }));
}
export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context)
        throw new Error('useTheme must be used inside <ThemeProvider>');
    return context;
}
//# sourceMappingURL=theme.js.map